import React from "react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import Counter from "@/components/Counter";
import { Section, SectionHeading, Eyebrow, Icon, PrimaryLink } from "@/components/common";
import { VALUES, STATS, CONTACT, PARENT, POSITIONING, BROCHURE, BG } from "@/data/content";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

export default function About() {
  const { t } = useLang();
  return (
    <div>
      <Seo title="About ERAM DIGITAL" description={POSITIONING} path="/about" />

      <Section className="py-20">
        <Reveal>
          <Eyebrow>About us</Eyebrow>
          <h1 className="max-w-4xl font-display text-5xl font-semibold leading-tight tracking-tighter text-[#0A0A0A] sm:text-6xl">
            The technology arm of a <span className="gradient-text">Gulf powerhouse.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#4B5563]" data-testid="about-positioning">{POSITIONING}</p>
        </Reveal>
      </Section>

      <Section className="pb-20">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-[#E5E7EB]">
              <img src={BG.team} alt="Industrial and technology collaboration — illustration from the ERAM DIGITAL brochure" width="1024" height="1167" className="aspect-[4/5] w-full object-cover" loading="lazy" data-testid="about-team-graphic" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-8">
              <div>
                <h3 className="font-display text-2xl font-semibold text-[#0A0A0A]">Our story</h3>
                <p className="mt-3 leading-relaxed text-[#4B5563]">
                  ERAM HOLDINGS began operations in the 1990s and grew into a global group headquartered in Al Khobar, Saudi Arabia, spanning 40+ companies, 125+ offices and nine business verticals. ERAM DIGITAL brings that regional depth to industrial, public and enterprise technology — connecting people, processes and systems from strategy to implementation.
                </p>
              </div>
              <div>
                <h3 className="font-display text-2xl font-semibold text-[#0A0A0A]">Our mission</h3>
                <p className="mt-3 leading-relaxed text-[#4B5563]" data-testid="about-mission">{BROCHURE.mission}</p>
              </div>
              <div>
                <h3 className="font-display text-2xl font-semibold text-[#0A0A0A]">Our vision</h3>
                <p className="mt-3 leading-relaxed text-[#4B5563]" data-testid="about-vision">{BROCHURE.vision}</p>
              </div>
            </div>
          </Reveal>
        </div>
      </Section>

      <div className="border-y border-[#E5E7EB] bg-[#F9FAFB]">
        <Section className="py-16">
          <div className="grid grid-cols-2 gap-8 lg:grid-cols-4">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.1} className="text-center">
                <p className="font-display text-5xl font-bold tracking-tight text-[#0A0A0A]"><Counter value={s.value} suffix={s.suffix} /></p>
                <p className="mt-2 text-sm text-[#4B5563]">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      <Section className="py-24">
        <Reveal><SectionHeading eyebrow="Our values" title="What we" gradient="stand for." center /></Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div className="gradient-border h-full rounded-2xl p-7">
                <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-xl gradient-bg text-white"><Icon name={v.icon} className="h-6 w-6" /></div>
                <h4 className="font-display text-lg font-semibold text-[#0A0A0A]">{v.title}</h4>
                <p className="mt-2 text-sm leading-relaxed text-[#4B5563]">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section className="pb-24">
        <Reveal><SectionHeading eyebrow="Why ERAM DIGITAL" title="Regional depth." gradient="Practical delivery." subtitle="Specialist teams, clear governance and a focus on the outcomes your organisation needs." /></Reveal>
        <div className="mt-12 grid gap-x-10 sm:grid-cols-2 lg:grid-cols-3" data-testid="brochure-differentiators">
          {BROCHURE.differentiators.map((item, i) => <div key={item.title} className="flex gap-4 border-t border-slate-200 py-7" data-testid={`differentiator-${i}`}>
            <Icon name={item.icon} className="mt-1 h-6 w-6 shrink-0 text-brand-violet" /><div><h3 className="font-display text-lg font-bold">{item.title}</h3><p className="mt-2 text-sm leading-relaxed text-slate-600">{item.desc}</p></div>
          </div>)}
        </div>
      </Section>

      {/* PARENT GROUP + VERTICALS */}
      <div className="border-y border-[#E5E7EB] bg-[#F9FAFB]">
        <Section className="py-24">
          <Reveal>
            <SectionHeading eyebrow="Part of ERAM HOLDINGS" title="One group," gradient="nine verticals." subtitle={PARENT.blurb} />
          </Reveal>
          <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {PARENT.verticals.map((v, i) => (
              <Reveal key={v} delay={i * 0.04}>
                <div className={`flex items-center gap-3 rounded-2xl border p-5 ${v.includes("ERAM DIGITAL") ? "border-brand-violet/50 bg-brand-violet/10" : "border-[#E5E7EB] bg-white"}`}>
                  <span className="h-2 w-2 shrink-0 rounded-full gradient-bg" />
                  <span className="text-sm font-medium text-[#0A0A0A]">{v}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.1}>
            <div className="mt-8 flex justify-center">
              <a href={PARENT.url} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] px-6 py-3 text-sm font-semibold text-[#0A0A0A] transition-colors hover:border-brand-violet hover:bg-[#F3F4F6]" data-testid="about-parent-link">
                Explore ERAM HOLDINGS
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
            </div>
          </Reveal>
        </Section>
      </div>

      <Section className="pb-24">
        <Reveal>
          <div className="rounded-3xl border border-[#E5E7EB] bg-[#F9FAFB] p-10 sm:p-14">
            <div className="grid items-center gap-8 lg:grid-cols-2">
              <div>
                <Eyebrow>Regional presence</Eyebrow>
                <h3 className="font-display text-3xl font-semibold text-[#0A0A0A]">Rooted in the Gulf, connected to the world.</h3>
                <p className="mt-4 leading-relaxed text-[#4B5563]" data-testid="about-regional-presence">Our head office is in Al Khobar, with regional offices in Dubai and Manama. The ERAM DIGITAL brochure reports {BROCHURE.countriesServed} countries served; ERAM HOLDINGS has a separate office footprint across 16 countries.</p>
              </div>
              <div className="space-y-4">
                <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
                  <p className="text-xs font-medium uppercase tracking-widest text-brand-teal">Headquarters</p>
                  <p className="mt-2 text-[#0A0A0A]">{CONTACT.hq}</p>
                </div>
                <div className="rounded-2xl border border-[#E5E7EB] bg-white p-6">
                  <p className="text-xs font-medium uppercase tracking-widest text-brand-teal">Regional offices</p>
                  <p className="mt-2 text-[#0A0A0A]">{CONTACT.uae} · {CONTACT.bahrain}</p>
                </div>
                <PrimaryLink to="/contact" testid="about-cta">{t("cta.consult")}</PrimaryLink>
              </div>
            </div>
          </div>
        </Reveal>
      </Section>
    </div>
  );
}
